import React from 'react';
import { styled as muiStyled } from '@mui/system';
import { TypographyProps } from '@mui/material';
import colors from '@/theme/colors';
import { Translate } from '../../components/Translate';
import {
  BodyLargeEmphasizedStyles,
  BodyLargeStyles,
  BodyMediumEmphasizedStyles,
  BodyMediumStyles,
  BodySmallEmphasizedStyles,
  BodySmallStyles,
  BodyLargeMidEmphasisdStyles,
  BodyMediumMidEmphasisStyles,
  BodySmallMidEmphasisStyles,
  DisplayLargeStyles,
  TitleLargeEmphasizedStyles,
  TitleLargeStyles,
  TitleMediumEmphasizedStyles,
  TitleMediumStyles,
  TitleSmallEmphasizedStyles,
  TitleSmallStyles,
  BodyMediumMidEmphasisUnderlineStyles,
  BodySmallMidEmphasisUnderlineStyles,
} from './Typography.styles';

// MUI filters 'color' as a system prop when 'as' is a native element (e.g. as="span").
// This wrapper overrides shouldForwardProp so 'color' always reaches the Text component.
const styled = (component: React.ComponentType<any>) =>
  muiStyled(component, {
    shouldForwardProp: prop => prop !== 'ownerState' && prop !== 'sx',
  });

type HTMLTags = 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label';

type TextProps = Omit<TypographyProps, 'ref'> & {
  'data-testid'?: string;
  className?: string;
  children: React.ReactNode;
  color?: string;
  nonTranslate?: boolean;
  htmlFor?: string;
  as?: HTMLTags;
};

const Text = React.forwardRef<any, TextProps>(
  (
    {
      className,
      children,
      color = colors.prussian,
      nonTranslate = false,
      'data-testid': dataTestId,
      htmlFor,
      as: Component = 'span',
      style,
      ...props
    },
    ref
  ) => {
    return (
      <Component
        ref={ref as any}
        className={className}
        style={{ color, userSelect: 'none', ...style }}
        data-testid={dataTestId}
        htmlFor={htmlFor}
        {...props}
      >
        {nonTranslate ? children : <Translate>{children}</Translate>}
      </Component>
    );
  }
);

const BodyText = React.forwardRef<any, TextProps>(({ as: Component = 'p', ...props }, ref) => (
  <Text as={Component} ref={ref} {...props} />
));

export const BodyLargeEmphasized = styled(BodyText)(BodyLargeEmphasizedStyles);
export const BodyLarge = styled(BodyText)(BodyLargeStyles);
export const BodyMediumEmphasized = styled(BodyText)(BodyMediumEmphasizedStyles);
export const BodyMedium = styled(BodyText)(BodyMediumStyles);
export const BodySmallEmphasized = styled(BodyText)(BodySmallEmphasizedStyles);
export const BodySmall = styled(BodyText)(BodySmallStyles);
export const DisplayLarge = styled(Text)(DisplayLargeStyles);
export const BodyLargeMidEmphasis = styled(BodyText)(BodyLargeMidEmphasisdStyles);
export const BodyMediumMidEmphasis = styled(BodyText)(BodyMediumMidEmphasisStyles);
export const BodyMediumMidEmphasisUnderline = styled(BodyText)(
  BodyMediumMidEmphasisUnderlineStyles
);
export const BodySmallMidEmphasis = styled(BodyText)(BodySmallMidEmphasisStyles);
export const BodySmallMidEmphasisUnderline = styled(BodyText)(BodySmallMidEmphasisUnderlineStyles);
export const TitleLargeEmphasized = styled(Text)(TitleLargeEmphasizedStyles);
export const TitleLarge = styled(Text)(TitleLargeStyles);
export const TitleMediumEmphasized = styled(Text)(TitleMediumEmphasizedStyles);
export const TitleMedium = styled(Text)(TitleMediumStyles);
export const TitleSmallEmphasized = styled(Text)(TitleSmallEmphasizedStyles);
export const TitleSmall = styled(Text)(TitleSmallStyles);
